import { serialize } from 'object-to-formdata';
import { useRecoilState, useRecoilValue } from 'recoil';
import { setExtra, captureException } from '@sentry/browser';
import useSWR from 'swr';
import { useMemo } from 'react';
import { http, get, remove } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';

import {
  EventAttributes,
  EventResponse,
  EventRequestStep1,
  PlaceAttributes,
  SocietyEventRequestStep2,
  CompanyEventRequestStep3,
} from 'lib/models/event';
import { httpHeadersState, userSecretState } from 'lib/atoms/userSecretAtom';

import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import {
  OpportunityAttributes,
  OpportunityResponse,
  TouchpointState,
} from 'lib/models/opportunity';
import { CohostResponse } from 'lib/models/cohost';
import { QuestionState } from 'lib/models/answers';
import { useAuth } from 'lib/providers/AuthProvider';
import { useNotification } from './useNotification';

interface EventSettings {
  enforceCapacity: boolean;
  capacity: number | null;
  enableWaitlist: boolean;
  enableForceWaitlist: boolean;
  showGuestList: boolean;
  id: string;
}
interface EventClone {
  timezone?: string;
  applicationDeadline?: Date | null | string;
  startDate: Date | null | string;
  endDate?: Date | string | null;
}

interface ReturnType {
  createEvent: ({
    touchpoint,
  }: {
    touchpoint: EventRequestStep1;
  }) => Promise<EventAttributes | undefined>;
  getEventResponse: (pathname: string) => Promise<EventAttributes>;
  updateEvent: ({
    touchpoint,
    id,
  }: {
    touchpoint: EventRequestStep1;
    id: string;
  }) => Promise<EventAttributes | undefined>;
  updateEventStep2: ({
    touchpoint,
    id,
  }: {
    touchpoint: SocietyEventRequestStep2;
    id: string;
  }) => Promise<EventAttributes | undefined>;
  updateEventStep3: ({
    capacity,
    enforceCapacity,
    questions_attributes,
    id,
    enabled,
    touchpointableId,
    university_required_state,
    study_year_required_state,
    degree_required_state,
    study_subject_required_state,
    cv_required_state,
    linkedin_profile_required_state,
    enableWaitlist,
    enableForceWaitlist,
    removedCustomQuestionIds,
  }: {
    capacity: number;
    questions_attributes: Array<{
      id?: string;
      title: string;
      placeholder?: string;
      state: QuestionState;
      question_type: string;
      isNew?: boolean;
    }>;
    enforceCapacity: boolean;
    enabled: boolean;
    id: string;
    touchpointableId: string;
    university_required_state: QuestionState;
    study_year_required_state: QuestionState;
    degree_required_state: QuestionState;
    study_subject_required_state: QuestionState;
    cv_required_state: QuestionState;
    linkedin_profile_required_state: QuestionState;
    enableWaitlist: boolean;
    enableForceWaitlist: boolean;
    removedCustomQuestionIds: Array<string>;
  }) => Promise<EventAttributes | undefined>;
  eventStateChangeHandler: ({
    state,
    id,
    unpublish,
  }: {
    state: TouchpointState;
    id: string;
    unpublish?: boolean;
  }) => Promise<EventAttributes | undefined>;
  getSocietyTouchpoints: (
    touchpointId: string,
    useCommonPoint?: boolean
  ) => Promise<OpportunityAttributes | undefined>;
  getState: (
    state: TouchpointState,
    cohost_application: CohostResponse | null | undefined
  ) => string;
  shouldPublishEvent: (id: string) => Promise<boolean | undefined>;
  onAddEventLink: ({
    id,
    online_link,
  }: {
    id: string;
    online_link: string;
  }) => Promise<OpportunityAttributes | undefined>;
  removeBanner: (id: string) => Promise<unknown>;
  onAddEventLocation: ({
    id,
    place_attributes,
  }: {
    id: string;
    place_attributes: PlaceAttributes;
  }) => Promise<OpportunityAttributes | undefined>;
  questionTypes: Record<string, { label: string; choice: number }>;
  updateCompanyEventStep3: ({
    touchpoint,
    id,
  }: {
    touchpoint: CompanyEventRequestStep3;
    id: string;
  }) => Promise<EventAttributes | undefined>;
  toggleCollectFeedback: (
    value: boolean,
    touchpointable_id: string,
    id: string
  ) => Promise<EventAttributes | undefined>;
  toggleNotifyMe: ({
    id,
    notify_creatable,
  }: {
    id: string;
    notify_creatable: boolean;
  }) => Promise<OpportunityAttributes | undefined>;
  eventSettings: ({
    data,
    id,
  }: {
    data: EventSettings;
    id: string;
  }) => Promise<EventAttributes | undefined>;
  cloneEvent: ({
    data,
    id,
  }: {
    data: EventClone;
    id: string;
  }) => Promise<EventAttributes | undefined>;
}

export const useEvent = (): ReturnType => {
  const notificationInstance = useNotification();
  const { headers, plainHeaders } = useRecoilValue(httpHeadersState);
  const [userSecret] = useRecoilState(userSecretState);
  const { isManager } = useAuth();
  const touchpountApi = isManager
    ? API_ROUTES.SOCIETY_TOUCHPOINTS
    : API_ROUTES.COMPANY_TOUCHPOINTS;

  const { data: questionTypesResponse } = useSWR<
    Record<string, { label: string; choice: number }>
  >(userSecret ? [API_ROUTES.CAMPAIGN_QUESTIONS_TYPES, headers] : null, get, {
    revalidateOnFocus: false,
  });

  const questionTypes = useMemo(() => {
    return questionTypesResponse?.question_types || {};
  }, [questionTypesResponse]);

  const createEvent = async ({
    touchpoint,
  }: {
    touchpoint: EventRequestStep1;
  }) => {
    const body = {
      touchpoint,
    };
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<EventResponse>(touchpountApi, {
        method: 'POST',
        body: formData,
        headers: plainHeaders,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('createEvent body', JSON.stringify(body));
      captureException(error);
    }
  };

  async function getEventResponse(pathname: string): Promise<EventAttributes> {
    const response = await get<EventResponse>(pathname, headers);
    const event = parseResponse<EventAttributes>(response);

    return event;
  }

  const updateEvent = async ({
    touchpoint,
    id,
  }: {
    touchpoint: EventRequestStep1;
    id: string;
  }) => {
    const body = {
      touchpoint,
    };

    const formData = serialize(body, {
      indices: false,
      allowEmptyArrays: true,
    });
    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: formData,
        headers: plainHeaders,
      });

      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateEvent body', JSON.stringify(body));
      captureException(error);
    }
  };

  const updateEventStep2 = async ({
    touchpoint,
    id,
  }: {
    touchpoint: SocietyEventRequestStep2;
    id: string;
  }) => {
    const body = {
      touchpoint,
    };

    const formData = serialize(body, { allowEmptyArrays: true });
    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: formData,
        headers: plainHeaders,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateEventStep2 body', JSON.stringify(body));
      captureException(error);
    }
  };

  const updateEventStep3 = async ({
    capacity,
    enforceCapacity,
    questions_attributes,
    id,
    enabled,
    touchpointableId,
    university_required_state,
    study_year_required_state,
    degree_required_state,
    study_subject_required_state,
    cv_required_state,
    linkedin_profile_required_state,
    enableWaitlist,
    enableForceWaitlist,
    removedCustomQuestionIds,
  }: {
    capacity: number;
    questions_attributes: Array<{
      id?: string;
      title: string;
      placeholder?: string;
      state: QuestionState;
      question_type: string;
      isNew?: boolean;
    }>;
    enforceCapacity: boolean;
    id: string;
    enabled: boolean;
    touchpointableId: string;
    university_required_state: QuestionState;
    study_year_required_state: QuestionState;
    degree_required_state: QuestionState;
    study_subject_required_state: QuestionState;
    cv_required_state: QuestionState;
    linkedin_profile_required_state: QuestionState;
    enableWaitlist: boolean;
    enableForceWaitlist: boolean;
    removedCustomQuestionIds: Array<string>;
  }) => {
    const body = {
      touchpoint: {
        touchpointable_attributes: {
          id: touchpointableId,
          capacity,
          enforce_capacity: enforceCapacity,
          waitlist: enableWaitlist,
          enforce_waitlist_for_all: enableForceWaitlist,
        },
        enabled,
        university_required_state,
        study_year_required_state,
        degree_required_state,
        study_subject_required_state,
        cv_required_state,
        linkedin_profile_required_state,
        state:
          enabled === false ? TouchpointState.DRAFT : TouchpointState.PUBLISHED,
        questions_attributes: [
          ...questions_attributes.map((ques) => ({
            ...ques,
            ...(!ques.isNew ? { id: ques.id } : { id: undefined }),
          })),
          ...removedCustomQuestionIds.map((id) => ({
            id: id,
            _destroy: true,
          })),
        ],
      },
    };

    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: headers,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateEventStep3 body', JSON.stringify(body));
      captureException(error);
    }
  };
  const toggleCollectFeedback = async (
    value: boolean,
    touchpointable_id: string,
    id: string
  ) => {
    const body = {
      touchpoint: {
        touchpointable_attributes: {
          id: touchpointable_id,
          collect_feedback: value,
        },
      },
    };

    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: headers,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('toggleCollectFeedback body', JSON.stringify(body));
      captureException(error);
    }
  };

  const eventStateChangeHandler = async ({
    state,
    id,
    unpublish,
  }: {
    state: TouchpointState;
    id: string;
    unpublish?: boolean;
  }) => {
    // To Publish event, touchpoint[state] = published and enabled = true
    // To Unpublish event, touchpoint[state] = draft and enabled = false
    let body;
    if (unpublish) {
      body = {
        touchpoint: {
          state: TouchpointState.DRAFT,
          enabled: false,
        },
      };
    } else {
      body = {
        touchpoint: {
          state:
            state === TouchpointState.DRAFT
              ? TouchpointState.PUBLISHED
              : TouchpointState.DRAFT,
          enabled: state === TouchpointState.DRAFT ? true : false,
        },
      };
    }
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: formData,
        headers: plainHeaders,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('eventStateChangeHandler body', JSON.stringify(body));
      captureException(error);
    }
  };

  const getSocietyTouchpoints = async (
    touchpointId: string,
    useCommonPoint?: boolean
  ) => {
    try {
      const api = useCommonPoint ? API_ROUTES.CAMPAIGNS : touchpountApi;
      const response = await get<OpportunityResponse>(
        `${api}/${touchpointId}`,
        headers
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getState = (
    state: TouchpointState,
    cohost_application: CohostResponse | null | undefined
  ) => {
    const getOtherStates = (state: string) => {
      if (state === TouchpointState.PUBLISHED) return 'Live';
      else if (state === TouchpointState.READY_FOR_APPROVAL) return 'In review';
      else if (state === TouchpointState.EXPIRED) return 'Ended';
      else return state;
    };
    if (cohost_application) {
      const { status } = parseResponse(cohost_application);
      if (status === TouchpointState.PENDING)
        return TouchpointState.INVITATION_PENDING as string;
      else {
        return getOtherStates(state);
      }
    } else {
      return getOtherStates(state);
    }
  };

  const shouldPublishEvent = async (id: string) => {
    try {
      const response = await getSocietyTouchpoints(id);
      if (response) {
        const { title, is_online, start_date, kind, description } = response;
        const address = parseResponse(response.place);
        const topics = parseArrayResponse(response.topics);
        if (
          title &&
          ((!is_online && address !== null) || is_online) &&
          start_date &&
          topics.length === 3 &&
          kind &&
          description
        )
          return true;
        else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onAddEventLink = async ({
    id,
    online_link,
  }: {
    id: string;
    online_link: string;
  }) => {
    const body = {
      touchpoint: {
        online_link,
      },
    };
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<OpportunityResponse>(
        `${touchpountApi}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('onAddEventLink body', JSON.stringify(body));
      captureException(error);
    }
  };

  const toggleNotifyMe = async ({
    id,
    notify_creatable,
  }: {
    id: string;
    notify_creatable: boolean;
  }) => {
    const body = {
      touchpoint: {
        notify_creatable,
      },
    };
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<OpportunityResponse>(
        `${touchpountApi}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('onAddEventLink body', JSON.stringify(body));
      captureException(error);
    }
  };

  const removeBanner = async (id: string): Promise<unknown> => {
    const pathname = `${touchpountApi}/${id}/remove_icon`;
    try {
      const response = await remove(pathname, headers);
      return response;
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  const onAddEventLocation = async ({
    id,
    place_attributes,
  }: {
    id: string;
    place_attributes: PlaceAttributes;
  }) => {
    const body = {
      touchpoint: {
        place_attributes,
      },
    };
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<OpportunityResponse>(
        `${touchpountApi}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('onAddEventLocation body', JSON.stringify(body));
      captureException(error);
    }
  };

  const updateCompanyEventStep3 = async ({
    touchpoint,
    id,
  }: {
    touchpoint: CompanyEventRequestStep3;
    id: string;
  }) => {
    const { questions_attributes, removedCustomQuestionIds } = touchpoint;
    const questionsAttributes = [
      ...questions_attributes?.map((ques) => ({
        ...ques,
        ...(!ques.isNew ? { id: ques.id } : { id: undefined }),
      })),
      ...(removedCustomQuestionIds
        ? removedCustomQuestionIds.map((id) => ({
            id: id,
            _destroy: true,
          }))
        : []),
    ];
    delete touchpoint?.removedCustomQuestionIds;
    const body = {
      touchpoint: {
        ...touchpoint,
        questions_attributes: questionsAttributes,
      },
    };
    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: headers,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateCompanyEventStep3 body', JSON.stringify(body));
      captureException(error);
    }
  };

  const eventSettings = async ({
    data,
    id,
  }: {
    data: EventSettings;
    id: string;
  }) => {
    const {
      enforceCapacity,
      capacity,
      enableWaitlist,
      enableForceWaitlist,
      showGuestList,
      id: touchpointableId,
    } = data;
    const body = {
      touchpoint: {
        touchpointable_attributes: {
          id: touchpointableId,
          capacity,
          enforce_capacity: enforceCapacity,
          waitlist: enableWaitlist,
          enforce_waitlist_for_all: enableForceWaitlist,
        },
        show_guest_list: showGuestList,
      },
    };

    try {
      const response = await http<EventResponse>(`${touchpountApi}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: headers,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('eventSettings body', JSON.stringify(body));
      captureException(error);
    }
  };

  const cloneEvent = async ({ data, id }: { data: EventClone; id: string }) => {
    const { timezone, applicationDeadline, startDate, endDate } = data;

    const body = {
      touchpoint: {
        timezone: timezone,
        application_deadline: applicationDeadline,
        touchpointable_attributes: {
          start_date: startDate,
          end_date: endDate,
        },
      },
    };
    try {
      const response = await http<EventResponse>(
        `${touchpountApi}/${id}${API_ROUTES.CLONE}`,
        {
          method: 'Post',
          headers: headers,
          body: JSON.stringify(body),
        }
      );

      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('cloneEvent body', JSON.stringify(body));
      captureException(error);
    }
  };

  return {
    createEvent,
    getEventResponse,
    updateEvent,
    updateEventStep2,
    updateEventStep3,
    eventStateChangeHandler,
    getSocietyTouchpoints,
    getState,
    shouldPublishEvent,
    onAddEventLink,
    removeBanner,
    onAddEventLocation,
    questionTypes,
    updateCompanyEventStep3,
    toggleCollectFeedback,
    toggleNotifyMe,
    eventSettings,
    cloneEvent,
  };
};
